import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Position } from "../../types";
import { Dimensions } from "./types";
import { getPopupPosition } from "./utils";

const initialDimensions = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  width: 0,
  height: 0
};

type Options = {
  hover: boolean;
  position: Position;
  closeDelay: number;
  boundingElementRef?: React.MutableRefObject<HTMLElement | null>;
};

export function useContextPopupState({
  hover,
  position,
  closeDelay,
  boundingElementRef
}: Options) {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchorDimensions] = useState<Dimensions>(initialDimensions);
  const [popup, setPopupDimensions] = useState<Dimensions>(initialDimensions);

  const openPopup = useCallback(() => setIsOpen(true), []);
  const closePopup = useCallback(() => setIsOpen(false), []);

  const boundingElement = boundingElementRef?.current || document.body;

  const setAdjustedPopupDimensions = useCallback(
    (popupDimensions: Dimensions) => {
      const popupPosition = getPopupPosition(position, {
        anchor,
        popup: popupDimensions,
        boundingRect: boundingElement.getBoundingClientRect()
      });
      setPopupDimensions({ ...popupDimensions, ...popupPosition });
    },
    [position, anchor, boundingElement]
  );

  const anchorRef = useElementDimensions(setAnchorDimensions);

  const {
    onMouseEnterAnchor,
    onMouseLeaveAnchor,
    onMouseEnterPopup,
    onMouseLeavePopup
  } = useHoverProps({
    openPopup,
    closePopup,
    closeDelay
  });

  const anchorProps = useMemo(() => {
    if (hover) {
      return {
        onClick: isOpen ? closePopup : openPopup,
        onMouseEnter: onMouseEnterAnchor,
        onMouseLeave: onMouseLeaveAnchor
      };
    } else {
      return {
        onClick: isOpen ? closePopup : openPopup
      };
    }
  }, [
    hover,
    onMouseEnterAnchor,
    onMouseLeaveAnchor,
    openPopup,
    closePopup,
    isOpen
  ]);

  const popupProps = useMemo(() => {
    if (hover) {
      return {
        onMouseEnter: onMouseEnterPopup,
        onMouseLeave: onMouseLeavePopup
      };
    } else {
      return {};
    }
  }, [hover, onMouseEnterPopup, onMouseLeavePopup]);

  return {
    openPopup,
    closePopup,
    isOpen,
    anchor,
    popup,
    setPopupDimensions: setAdjustedPopupDimensions,
    anchorRef,
    anchorProps,
    popupProps
  };
}

type HoverPropsArgs = {
  openPopup: () => void;
  closePopup: () => void;
  closeDelay: number;
};

function useHoverProps({ openPopup, closePopup, closeDelay }: HoverPropsArgs) {
  const mouseInAnchor = useRef(false);
  const mouseInPopup = useRef(false);
  const closeTimeout = useRef<any>(null);

  const closeAfterDelay = useCallback(() => {
    clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(() => {
      if (!mouseInAnchor.current && !mouseInPopup.current) {
        closePopup();
      }
    }, closeDelay);
  }, [closeDelay, closePopup]);

  const onMouseEnterAnchor = useCallback(() => {
    openPopup();
    mouseInAnchor.current = true;
  }, [openPopup]);

  const onMouseLeaveAnchor = useCallback(() => {
    mouseInAnchor.current = false;
    closeAfterDelay();
  }, [closeAfterDelay]);

  const onMouseEnterPopup = useCallback(() => {
    mouseInPopup.current = true;
  }, []);

  const onMouseLeavePopup = useCallback(() => {
    mouseInPopup.current = false;
    closeAfterDelay();
  }, [closeAfterDelay]);

  return {
    onMouseEnterAnchor,
    onMouseLeaveAnchor,
    onMouseEnterPopup,
    onMouseLeavePopup
  };
}

type AutoCloseArgs = {
  isOpen: boolean;
  closePopup: () => void;
  closeOnPopupClick: boolean;
  popupRef: any;
};

export function useAutoCloseOnClick({
  isOpen,
  closePopup,
  closeOnPopupClick,
  popupRef
}: AutoCloseArgs) {
  const closeOnClickOutside = useCallback(
    (event: MouseEvent) => {
      if (closeOnPopupClick || !popupRef.current.contains(event.target)) {
        closePopup();
      }
    },
    [closeOnPopupClick, popupRef, closePopup]
  );

  useEffect(() => {
    // timeout needed so initial open click doesn't immediately close popup again
    const timeout = setTimeout(() => {
      if (isOpen) {
        window.addEventListener("click", closeOnClickOutside);
      } else {
        window.removeEventListener("click", closeOnClickOutside);
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("click", closeOnClickOutside);
    };
  }, [isOpen, closeOnClickOutside]);
}

export function useElementDimensions(onLayout: (arg0: Dimensions) => void) {
  const ref = useRef<any>();
  const dimensions = getDimensions(ref);
  const { height, width, left, top, right, bottom } = dimensions;
  useLayoutEffect(() => {
    ref.current &&
      onLayout({
        height,
        width,
        left,
        top,
        right,
        bottom
      });
  }, [onLayout, height, width, left, top, bottom, right]);
  return ref;
}

function getDimensions(ref?: any): Dimensions {
  if (!ref || !ref.current) {
    return {
      height: 0,
      width: 0,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    };
  }

  return ref.current.getBoundingClientRect();
}
